import React, { useCallback, useEffect, useRef, useState } from 'react';
import voiceChat, {
  VoiceChatState,
  VoicePhase,
  VoiceTranscriptEvent,
} from '../services/voiceChat';
import type { VoiceCardGroup } from '../components/F8/VoiceCardOverlay';
import { capture } from '../services/posthog';
import { VoiceChatContext } from '../hooks/useVoiceChat';

// Carl's call, 2026-09-13 (PPL-534): the voice card stack shows at most this many groups.
const MAX_VOICE_CARD_GROUPS = 4;

// This file must export only React components (Fast Refresh) — the context
// object and the useVoiceChat hook live in ../hooks/useVoiceChat.

/**
 * Map the raw quota reason (an internal server/transport string that
 * voiceChat.ts deliberately keeps out of the UI) to a closed analytics enum.
 * Returns null for infra/misconfig markers ('not configured', 'not found') and
 * anything unrecognised — those carry no genuine upgrade context, so no
 * upgrade-prompt event is emitted. The raw string is never sent to PostHog.
 */
function upgradePromptReason(
  raw: string | null | undefined,
): 'free_sessions_exhausted' | 'monthly_minutes_exhausted' | null {
  const r = (raw || '').toLowerCase();
  if (!r) return null;
  // Server misconfiguration / lookup failures are not a quota/upgrade signal.
  if (r.includes('not configured') || r.includes('not found')) return null;
  if (r.includes('exhausted') || r.includes('session')) return 'free_sessions_exhausted';
  if (r.includes('limit') || r.includes('minute') || r.includes('monthly')) {
    return 'monthly_minutes_exhausted';
  }
  return null;
}

export function VoiceChatProvider({ children }: { children: React.ReactNode }) {
  const [state, setState] = useState<VoiceChatState>('idle');
  const [phase, setPhase] = useState<VoicePhase>('listening');
  const [muted, setMuted] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const [lastError, setLastError] = useState<string | null>(null);
  const [remainingSeconds, setRemainingSeconds] = useState<number | null>(null);
  const [tier, setTier] = useState<string | null>(null);
  const [userTranscript, setUserTranscript] = useState('');
  const [agentTranscript, setAgentTranscript] = useState('');
  const [activeCards, setActiveCards] = useState<VoiceCardGroup[]>([]);
  const [timeWarningSeconds, setTimeWarningSeconds] = useState<number | null>(null);
  const [quotaExhausted, setQuotaExhausted] = useState(false);
  const [quotaReason, setQuotaReason] = useState<string | null>(null);
  // PPL-534 — the thread this session's transcript and skill cards were written
  // to. Survives the hang-up so F8Chat can reopen it and show the card that was
  // on screen (a draft above all) as a persisted message.
  const [voiceThreadId, setVoiceThreadId] = useState<string | null>(null);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    voiceChat.on({
      onState: (s) => setState(s),
      onPhase: (p) => setPhase(p),
      onTranscript: (e: VoiceTranscriptEvent) => {
        if (e.sender === 'user') setUserTranscript(e.text);
        else setAgentTranscript(e.text);
      },
      onError: (msg) => setLastError(msg),
      onSessionInfo: (info) => {
        setTier(info.tier);
        setRemainingSeconds(info.remainingSeconds);
        if (info.threadId) setVoiceThreadId(info.threadId);
      },
      onCard: (cards, skillName) => {
        const group: VoiceCardGroup = {
          timestamp: Date.now(),
          skillName,
          cards: cards as any,
        };
        // PPL-534 — nothing expires a card any more, so a long session would
        // stack them without limit. Keep the newest MAX_VOICE_CARD_GROUPS; the
        // older groups are already persisted on the thread (the proxy writes
        // every skill turn), so dropping them here loses nothing.
        setActiveCards((prev) => [...prev, group].slice(-MAX_VOICE_CARD_GROUPS));
      },
      onTimeWarning: (s) => {
        setTimeWarningSeconds(s);
        // Auto-clear after the window has passed
        setTimeout(() => setTimeWarningSeconds(null), s * 1000);
      },
      onQuotaExhausted: (reason) => {
        setQuotaExhausted(true);
        setQuotaReason(reason);
        // Ineligibility drives the WebVoiceMode upgrade prompt — count it shown,
        // but only for genuine quota reasons (never infra/misconfig markers) and
        // never with the raw internal reason string.
        const promptReason = upgradePromptReason(reason);
        if (promptReason) {
          capture('voice_upgrade_prompt_shown', {
            reason: promptReason,
            surface_ui: 'web_voice_mode',
            platform: 'web',
          });
        }
      },
    });
  }, []);

  // Cards are never auto-dismissed (PPL-534) — they close on the card's ×, on
  // a card action, or when the next session starts (see `start` below).

  // Connection elapsed timer; also ticks down remainingSeconds
  useEffect(() => {
    if (state === 'connected') {
      const start = Date.now();
      const initialRemaining = remainingSeconds;
      setElapsed(0);
      timerRef.current = setInterval(() => {
        const e = Math.floor((Date.now() - start) / 1000);
        setElapsed(e);
        if (initialRemaining !== null && initialRemaining > 0) {
          setRemainingSeconds(Math.max(0, initialRemaining - e));
        }
      }, 500);
    } else {
      if (timerRef.current) clearInterval(timerRef.current);
      timerRef.current = null;
    }
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state]);

  /**
   * `threadId` resumes an existing f8 chat thread instead of letting the proxy
   * open a fresh one, so the transcript and every skill card land in the thread
   * the caller is showing (PPL-534).
   *
   * `setActiveCards([])` belongs here and not in the hang-up path: a card must
   * outlive the call it was emitted in, and is only stale once a new session
   * begins.
   */
  const start = useCallback(async (options?: { threadId?: string | null }) => {
    setLastError(null);
    setUserTranscript('');
    setAgentTranscript('');
    setActiveCards([]);
    setTimeWarningSeconds(null);
    setQuotaExhausted(false);
    setQuotaReason(null);
    setVoiceThreadId(options?.threadId || null);
    try {
      await voiceChat.connect({ threadId: options?.threadId || null });
    } catch (err: any) {
      const msg = err?.message || 'Failed to connect';
      // onQuotaExhausted listener already set the quota flag if applicable;
      // only set generic error if it isn't a quota issue.
      if (!quotaExhausted) {
        setLastError(msg);
      }
    }
  }, [quotaExhausted]);

  const stop = useCallback(async () => {
    await voiceChat.disconnect();
  }, []);

  const toggleMute = useCallback(() => {
    setMuted((m) => {
      const next = !m;
      voiceChat.setMuted(next);
      return next;
    });
  }, []);

  const dismissCard = useCallback((timestamp: number) => {
    setActiveCards((prev) => prev.filter((g) => g.timestamp !== timestamp));
  }, []);

  const sendChatHistory = useCallback((messages: any[]) => {
    if (!Array.isArray(messages) || messages.length === 0) return;
    voiceChat.sendContextUpdate({ chatHistory: messages });
  }, []);

  return (
    <VoiceChatContext.Provider
      value={{
        state,
        phase,
        muted,
        elapsed,
        lastError,
        remainingSeconds,
        tier,
        userTranscript,
        agentTranscript,
        activeCards,
        timeWarningSeconds,
        quotaExhausted,
        quotaReason,
        voiceThreadId,
        start,
        stop,
        toggleMute,
        dismissCard,
        sendChatHistory,
      }}
    >
      {children}
    </VoiceChatContext.Provider>
  );
}
